import { notNull } from 'utils/type_guards';

import Product from 'domains/product';

import type { Dictionary } from '@reduxjs/toolkit';
import type { CanvasConfiguration } from 'domains/canvases';
import type { SavedArrangementCanvas } from 'domains/saved_arrangements';
import type { Point } from 'types/common_types';

/**
 * An Arrangement is a set of canvases, which identify a configuration of
 * products & options, as well as an (x, y) position
 *
 * All sizing & positioning data use inches as their unit, only applying the PPI
 * when rendered to the screen or sent to a render service in the case of
 * saved wall previews & saved arrangements
 */
export default class Arrangement {
  private _canvases: CanvasConfiguration[];
  private _canvasProducts: Dictionary<Product> = {};

  constructor(canvases: CanvasConfiguration[]) {
    this._canvases = [...canvases];
  }

  static createFromSavedArrangement(canvases: SavedArrangementCanvas[]) {
    return new Arrangement(canvases.map((sc) => ({ ...sc.config })));
  }

  static createWithProducts(canvases: { canvas: CanvasConfiguration; product?: Product }[]) {
    const arrangement = new Arrangement([]);

    canvases.forEach((c) => {
      arrangement.addCanvas(c.canvas, c.product);
    });

    return arrangement;
  }

  get canvases() {
    return this._canvases;
  }

  get leftmost() {
    return this._canvases.reduce(
      (value: CanvasConfiguration, current) =>
        value.position.x < current.position.x ? value : current,
      this._canvases[0]
    );
  }

  get topmost() {
    return this._canvases.reduce(
      (value: CanvasConfiguration, current) =>
        value.position.y < current.position.y ? value : current,
      this._canvases[0]
    );
  }

  get width() {
    return this._calculateDimension('width');
  }

  get height() {
    return this._calculateDimension('height');
  }

  get orientation() {
    if (this.width === this.height) return 'square';
    if (this.width > this.height) return 'landscape';

    return 'portrait';
  }

  find(id: string) {
    return this._canvases.find((c) => c.id === id);
  }

  getProduct(id: string) {
    return this._canvasProducts[id];
  }

  setProduct(id: string, product: Product) {
    this._canvasProducts[id] = product;
  }

  addCanvas(canvas: CanvasConfiguration, product?: Product) {
    this._canvases.push(canvas);

    if (product) {
      this._canvasProducts[canvas.id] = product;
    }
  }

  removeCanvas(id: string) {
    this._canvases = this._canvases.filter((c) => c.id !== id);
    delete this._canvasProducts[id];
  }

  /**
   * Recalculate the top-left point of the entire arrangement given the previous
   * point, taking into account the current topmost & leftmost
   */
  recalculateTopLeft(old: Point): Point {
    if (this._canvases.length === 0) return old;

    return {
      x: old.x + this.leftmost.position.x,
      y: old.y + this.topmost.position.y,
    };
  }

  /**
   * Recalculate all canvas positions to be relative to the topmost & leftmost,
   * while setting the topmost & leftmost to the 0 point of their respective direction
   *
   * Returns canvases as an array of redux entity adapter update objects
   */
  recalculatePositions() {
    this._canvases = this._canvases.map((c) => ({
      ...c,
      position: {
        x: c.position.x - this.leftmost.position.x,
        y: c.position.y - this.topmost.position.y,
      },
    }));

    return this._canvases.map((c) => ({ id: c.id, changes: { position: c.position } }));
  }

  private _calculateDimension(dimension: 'width' | 'height') {
    // First we find the rightmost & bottommost points of each canvas
    // using their current top-left x, y position & calculated size
    const points = this._canvases
      .map((c) => {
        const product = this.getProduct(c.id);
        if (!product) return null;

        return {
          x: c.position.x + product.width,
          y: c.position.y + product.height,
        };
      })
      .filter(notNull);

    // If we didn't have enough data to calculate all the points, width won't be accurate
    if (points.length !== this._canvases.length || points.length === 0) return 0;

    const axis = dimension === 'width' ? 'x' : 'y';
    const start = dimension === 'width' ? this.leftmost.position.x : this.topmost.position.y;

    const end = points.reduce(
      (value: number, current) => Math.max(value, current[axis]),
      points[0][axis]
    );

    return Math.abs(end - start);
  }
}
